/**
 * AgentFace - the team's faces: a round, friendly character per agent, drawn inline (no image files).
 * Draper has a hair swoosh, Peggy a fringe, Pete a cap, Rogers a headset, Harry round glasses, Claw an antenna.
 * Moods: idle (soft smile, blinks now and then), thinking (looks up, small "o", thought dots, gentle bob), happy (closed eyes, big smile).
 * Animations live in index.css (.agent-face-*) and stop under prefers-reduced-motion.
 */

export type FaceAgent = "draper" | "peggy" | "pete" | "rogers" | "harry" | "claw";
export type FaceMood = "idle" | "thinking" | "happy";

const PALETTE: Record<FaceAgent, { face: string; accent: string }> = {
  draper: { face: "#7cb4fb", accent: "#1d4ed8" },
  peggy: { face: "#f9a8d4", accent: "#db2777" },
  pete: { face: "#fcd34d", accent: "#d97706" },
  rogers: { face: "#6ee7b7", accent: "#059669" },
  harry: { face: "#c4b5fd", accent: "#7c3aed" },
  claw: { face: "#67e8f9", accent: "#0891b2" },
};

const NAMES: Record<FaceAgent, string> = { draper: "Draper", peggy: "Peggy", pete: "Pete", rogers: "Rogers", harry: "Harry", claw: "Claw" };

const INK = "#1e293b";

export function asFaceAgent(name: string | null | undefined): FaceAgent {
  const a = String(name || "").toLowerCase();
  return (a in PALETTE ? a : "draper") as FaceAgent;
}

export function AgentFace({ agent, mood = "idle", size = 28, className = "", title }: {
  agent: FaceAgent | string;
  mood?: FaceMood;
  size?: number;
  className?: string;
  title?: string;
}) {
  const who = asFaceAgent(agent);
  const { face, accent } = PALETTE[who];
  const look = mood === "thinking" ? { dx: 1.5, dy: -2 } : { dx: 0, dy: 0 };
  const label = title ?? `${NAMES[who]}${mood === "thinking" ? " is working" : ""}`;

  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      role="img"
      aria-label={label}
      className={`agent-face agent-face-${mood} shrink-0 ${className}`}
      data-testid={`agent-face-${who}`}
      data-mood={mood}
    >
      <title>{label}</title>
      <g className="agent-face-body">
        {who === "claw" && (
          <>
            <line x1="32" y1="12" x2="32" y2="4" stroke={accent} strokeWidth="2.5" strokeLinecap="round" />
            <circle cx="32" cy="4.5" r="3" fill={accent} />
          </>
        )}
        {who === "rogers" && <path d="M9 34 C9 14 55 14 55 34" fill="none" stroke={accent} strokeWidth="3.5" />}

        <circle cx="32" cy="35" r="23" fill={face} />

        {who === "draper" && <path d="M11 31 C11 15 27 9 40 11 C50 13 55 21 53 29 C47 21 35 18 25 22 C18 25 14 28 11 31Z" fill={accent} />}
        {who === "peggy" && <path d="M9 36 C8 14 56 14 55 36 C51 27 44 23 32 25 C20 23 13 27 9 36Z" fill={accent} />}
        {who === "pete" && (
          <>
            <path d="M12 27 C14 13 50 13 52 27Z" fill={accent} />
            <rect x="34" y="24" width="24" height="5" rx="2.5" fill={accent} />
          </>
        )}
        {who === "rogers" && (
          <>
            <rect x="5" y="30" width="7" height="12" rx="3.5" fill={accent} />
            <rect x="52" y="30" width="7" height="12" rx="3.5" fill={accent} />
            <path d="M55 42 Q55 52 42 52" fill="none" stroke={accent} strokeWidth="2" />
            <circle cx="41" cy="52" r="2.5" fill={accent} />
          </>
        )}

        {mood === "happy" ? (
          <>
            <path d="M20 37 Q24 32 28 37" fill="none" stroke={INK} strokeWidth="2.6" strokeLinecap="round" />
            <path d="M36 37 Q40 32 44 37" fill="none" stroke={INK} strokeWidth="2.6" strokeLinecap="round" />
          </>
        ) : (
          <g className="agent-face-eyes">
            <ellipse cx={24 + look.dx} cy={36 + look.dy} rx="3.2" ry="4.2" fill={INK} />
            <ellipse cx={40 + look.dx} cy={36 + look.dy} rx="3.2" ry="4.2" fill={INK} />
            <circle cx={25 + look.dx} cy={34.5 + look.dy} r="1.1" fill="#fff" />
            <circle cx={41 + look.dx} cy={34.5 + look.dy} r="1.1" fill="#fff" />
          </g>
        )}

        {who === "harry" && (
          <>
            <circle cx="24" cy="36" r="7" fill="none" stroke={accent} strokeWidth="2.2" />
            <circle cx="40" cy="36" r="7" fill="none" stroke={accent} strokeWidth="2.2" />
            <line x1="31" y1="36" x2="33" y2="36" stroke={accent} strokeWidth="2.2" />
          </>
        )}

        <ellipse cx="18" cy="44" rx="4" ry="2.4" fill="#fb7185" opacity="0.45" />
        <ellipse cx="46" cy="44" rx="4" ry="2.4" fill="#fb7185" opacity="0.45" />

        {mood === "happy" && <path d="M25 45 Q32 54 39 45Z" fill={INK} />}
        {mood === "idle" && <path d="M27 46 Q32 50 37 46" fill="none" stroke={INK} strokeWidth="2.4" strokeLinecap="round" />}
        {mood === "thinking" && (
          <>
            <circle cx="34" cy="47" r="2.2" fill={INK} />
            <circle className="agent-face-dot1" cx="52" cy="14" r="2" fill={accent} />
            <circle className="agent-face-dot2" cx="57" cy="8" r="3" fill={accent} />
          </>
        )}
      </g>
    </svg>
  );
}
